import { useState, useEffect } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Snackbar,
  Alert,
} from '@mui/material';
import { Transition } from '../../../../components/Transition';
import { useAppDispatch } from '../../../../store';
import { createCustomer } from '../../../../store/users/api';
import { CreateCustomer } from '../../../../store/users/api/interfaces';
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE } from '../../../../constants/countryCodes';

interface CreateCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (customer: any) => void;
}

const CreateCustomerDialog: React.FC<CreateCustomerDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [countryCallingCode, setCountryCallingCode] = useState(DEFAULT_COUNTRY_CODE);
  const [gender, setGender] = useState<'male' | 'female' | ''>('');

  // Errors
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      // Reset all fields
      setFirstName('');
      setLastName('');
      setEmail('');
      setMobileNumber('');
      setCountryCallingCode(DEFAULT_COUNTRY_CODE);
      setGender('');
      setErrors({});
    }
  }, [open]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\d+$/.test(mobileNumber)) {
      newErrors.mobileNumber = 'Mobile number must contain only digits';
    }

    if (!countryCallingCode) {
      newErrors.countryCallingCode = 'Country calling code is required';
    }

    // Validate email format if provided
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please provide a valid email address';
    }

    // Validate gender if provided
    if (gender && gender !== 'male' && gender !== 'female') {
      newErrors.gender = 'Gender must be either male or female';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setShowError(false);

    try {
      const customerData: CreateCustomer = {
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        email: email.trim() || undefined,
        mobileNumber: mobileNumber.trim(),
        countryCallingCode,
        countryCode: 'QA', // Default to Qatar (same as user creation)
        gender: gender || undefined,
        language: 'en', // Default to 'en'
      };

      const response = await dispatch(createCustomer(customerData)).unwrap();

      if (response?.user) {
        setShowSuccess(true);
        // Call onSuccess with the created customer
        setTimeout(() => {
          onSuccess(response.user);
          onClose();
        }, 500);
      }
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to create customer');
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <>
      <Dialog
        fullWidth
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby='create-customer-dialog-description'
      >
        <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>
          Create New Customer
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          autoComplete='off'
        >
          <DialogContent dividers>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                sm={12}
              >
                <TextField
                  id='firstName'
                  name='firstName'
                  label='First name'
                  fullWidth
                  variant='outlined'
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (errors.firstName) {
                      setErrors((prev) => ({ ...prev, firstName: '' }));
                    }
                  }}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
              >
                <FormControl
                  fullWidth
                  error={!!errors.gender}
                >
                  <InputLabel>Gender</InputLabel>
                  <Select
                    id='gender'
                    name='gender'
                    value={gender || ''}
                    label='Gender'
                    onChange={(e) => {
                      setGender(e.target.value as 'male' | 'female' | '');
                      if (errors.gender) {
                        setErrors((prev) => ({ ...prev, gender: '' }));
                      }
                    }}
                    disabled={isSubmitting}
                  >
                    <MenuItem value={'male'}>Male</MenuItem>
                    <MenuItem value={'female'}>Female</MenuItem>
                  </Select>
                  {errors.gender && (
                    <FormHelperText>{errors.gender}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
              >
                <TextField
                  id='lastName'
                  name='lastName'
                  label='Last name'
                  fullWidth
                  variant='outlined'
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={4}
              >
                <FormControl 
                  fullWidth
                  error={!!errors.countryCallingCode}
                >
                  <InputLabel>Country Code</InputLabel>
                  <Select
                    id="countryCallingCode"
                    name="countryCallingCode"
                    value={countryCallingCode || DEFAULT_COUNTRY_CODE}
                    label="Country Code"
                    onChange={(e) => {
                      setCountryCallingCode(e.target.value);
                      if (errors.countryCallingCode) {
                        setErrors((prev) => ({ ...prev, countryCallingCode: '' }));
                      }
                    }}
                    disabled={isSubmitting}
                  >
                    {COUNTRY_CODES.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.code} ({country.country})
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.countryCallingCode && (
                    <FormHelperText>{errors.countryCallingCode}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={8}
              >
                <TextField
                  id='mobileNumber'
                  name='mobileNumber'
                  label='Mobile Number'
                  fullWidth
                  variant='outlined'
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                    setMobileNumber(value);
                    if (errors.mobileNumber) {
                      setErrors((prev) => ({ ...prev, mobileNumber: '' }));
                    }
                  }}
                  value={mobileNumber}
                  error={!!errors.mobileNumber}
                  helperText={errors.mobileNumber}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
              >
                <TextField
                  id='email'
                  name='email'
                  label={'E-mail'}
                  fullWidth
                  variant='outlined'
                  type='email'
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) {
                      setErrors((prev) => ({ ...prev, email: '' }));
                    }
                  }}
                  error={!!errors.email}
                  helperText={errors.email}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleClose}
              variant='outlined'
              disabled={isSubmitting}
            >
              {'Cancel'}
            </Button>
            <LoadingButton
              type='submit'
              loading={isSubmitting}
              variant='contained'
            >
              {'Save'}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={1500}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Saved successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={showError}
        autoHideDuration={3000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateCustomerDialog;

